import * as React from "react";

import { GridDisplay } from "@impero-com/common/components/form/legacyFields/gridDisplay";
import { assertNever } from "@impero-com/common/utils/obj";
import { nullToEmpty } from "@impero-com/common/utils/string";
import { Checkbox } from "antd";

import {
    castValue,
    CustomizedValidation,
    EditFormCreate,
    FormComponentProps,
    ValidationRule,
    validationRulesConverter,
} from "utils/form";

import { BaseFormDisplay } from "./formDisplay";
import * as theme from "./theme.css";

import { BaseFormField, EditInput, EditProps } from ".";

/**
 * The definition of an Option on a Checkbox element
 * @field value the "real", "inner" value of the Checkbox field once selected (to be sent in a JSON probably)
 * @field label the label that the Checkbox will show for the user to select
 * @field disabled should the Checkbox be greyed out
 */
interface CheckboxInputOption {
    value: string;
    label: string;
    disabled?: boolean;
}

interface CreateCheckboxInputProps<
    ID extends string,
    R,
> extends FormComponentProps<ID, string[], R> {
    id: ID;
    initialValue: string[] | undefined;
    label?: string;
    validators?: ValidationRule<string[] | undefined>[];
    options: CheckboxInputOption[];
    columns?: number;
    className?: string;
    validationError?: CustomizedValidation;
}

interface CheckboxInputState {
    options: CheckboxInputOption[];
}

class CreateCheckboxInput<ID extends string, R> extends React.PureComponent<
    CreateCheckboxInputProps<ID, R>,
    CheckboxInputState
> {
    render() {
        const { label, validationError } = this.props;

        return (
            <BaseFormField
                getComponent={() => this.getComponent(this.props.options)}
                hasFeedback={false}
                label={label}
                validationError={validationError}
            />
        );
    }

    getComponent = (options: CheckboxInputOption[]) => {
        const { form, id, validators, label, initialValue, className } =
            this.props;
        const { getFieldDecorator } = form;
        return getFieldDecorator(id, {
            rules: validationRulesConverter<string[]>()(
                id,
                form,
                validators,
                label
            ),
            valuePropName: "value",
            trigger: "onChange",
            validateFirst: true,
            preserve: true,
            initialValue: castValue(id, form, initialValue),
        })(
            <Checkbox.Group
                className={`${theme.imperoGrid} ${nullToEmpty(className)}`}
            >
                <GridDisplay
                    columns={this.props.columns ? this.props.columns : 1}
                    items={options}
                    renderItem={renderCheckboxButton}
                />
            </Checkbox.Group>
        );
    };
}

type EditCheckboxInputProps = EditProps<string[] | undefined>;

const EditCheckboxInputForm = EditFormCreate<
    string[],
    EditCheckboxInputProps
>()(CreateCheckboxInput);

const EditCheckboxInput = EditInput<string[], EditCheckboxInputProps>(
    EditCheckboxInputForm
);

interface CheckboxInputCommonProps {
    label?: string;
    options: CheckboxInputOption[];
    columns?: number;
    className?: string;
}

class ReadOnlyCheckboxInput extends React.PureComponent<
    CheckboxInputCommonProps & { value?: string[] }
> {
    render = () => {
        const { label, value, className } = this.props;
        return (
            <BaseFormDisplay label={label}>
                <Checkbox.Group
                    disabled
                    value={value}
                    className={`${theme.imperoGrid} ${nullToEmpty(className)}`}
                >
                    <GridDisplay
                        columns={this.props.columns ? this.props.columns : 1}
                        items={this.props.options}
                        renderItem={renderCheckboxButton}
                    />
                </Checkbox.Group>
            </BaseFormDisplay>
        );
    };
}

/**
 * Renders a CheckboxInputOption as a Checkbox. For use in GridDisplay.
 */
function renderCheckboxButton(opt: CheckboxInputOption): React.ReactNode {
    return (
        <Checkbox key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
        </Checkbox>
    );
}

type CheckboxInputProps<ID extends string, R> =
    | ({ type: "READ_ONLY" } & CheckboxInputCommonProps & { value?: string[] })
    | ({ type: "EDIT" } & EditCheckboxInputProps & CheckboxInputCommonProps)
    | ({ type: "CREATE" } & CreateCheckboxInputProps<ID, R> & {
              columns?: number;
          });

class CheckboxInput<ID extends string, R> extends React.PureComponent<
    CheckboxInputProps<ID, R>
> {
    render = () => {
        switch (this.props.type) {
            case "READ_ONLY": {
                const { type: _type, ...rest } = this.props;
                return <ReadOnlyCheckboxInput {...rest} />;
            }
            case "EDIT": {
                const { type: _type, ...rest } = this.props;
                return <EditCheckboxInput {...rest} />;
            }
            case "CREATE": {
                const { type: _type, ...rest } = this.props;
                return <CreateCheckboxInput {...rest} />;
            }
            default:
                return assertNever(this.props);
        }
    };
}
export type {
    CheckboxInputProps,
    CheckboxInputOption,
    CheckboxInputCommonProps,
    EditCheckboxInputProps,
    CreateCheckboxInputProps,
};
export { CheckboxInput };
